import { infiniteQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';

import type { WORKFLOW_TYPE } from '@/types/workflowConst';

import { getNotification } from './get-notification';
import { getNotificationHistory } from './get-notification-history';
import { getNotifications } from './get-notifications';

export const notificationQueryKeys = {
  all: ['notification'] as const,
  lists: () => [...notificationQueryKeys.all, 'list'] as const,
  list: (types: WORKFLOW_TYPE[]) =>
    [...notificationQueryKeys.lists(), types] as const,
  details: () => [...notificationQueryKeys.all, 'detail'] as const,
  detail: (alias: string) =>
    [...notificationQueryKeys.details(), alias] as const,
  histories: () => [...notificationQueryKeys.all, 'history'] as const,
  history: (ids: string[]) =>
    [...notificationQueryKeys.histories(), ids] as const,
};

/**
 * 알림 목록 조회 queryOptions
 */
export function notificationListQueryOptions(types: WORKFLOW_TYPE[]) {
  return queryOptions({
    queryKey: notificationQueryKeys.list(types),
    queryFn: () => getNotifications({ types }),
  });
}

/**
 * 알림 목록 조회 hook
 */
export function useNotificationListQuery(
  types: WORKFLOW_TYPE[],
  options?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery({
    ...notificationListQueryOptions(types),
    ...options,
  });
}

/**
 * 알림 상세 조회 queryOptions
 */
export function notificationDetailQueryOptions(notificationId: string) {
  return queryOptions({
    queryKey: notificationQueryKeys.detail(notificationId),
    queryFn: () => getNotification({ notification_id: notificationId }),
  });
}

/**
 * 알림 상세 조회 hook
 */
export function useNotificationDetailQuery(
  notificationId: string,
  options?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery({
    ...notificationDetailQueryOptions(notificationId),
    ...options,
  });
}

/**
 * 알림 히스토리 조회 infiniteQueryOptions
 * - offset 기반 페이지네이션
 */
export function notificationHistoryInfiniteQueryOptions(
  ids: string[],
  limit: number = 100,
) {
  return infiniteQueryOptions({
    queryKey: [...notificationQueryKeys.history(ids), 'infinite'] as const,
    queryFn: ({ pageParam }) =>
      getNotificationHistory({ ids, offset: pageParam, limit }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // lastPage가 limit보다 작으면 더 이상 없음
      if (lastPage.length < limit) return undefined;
      return allPages.flat().length;
    },
    enabled: ids.length > 0,
  });
}
